import type { DocumentExportConfig } from '../../types/documentExport'
import { fmtMoeda, type ProjecaoNr1 } from '../../utils/calculadoraNr1'

export type BuildCalculadoraNr1DocumentInput = {
  vidas: string
  valorIndividual: string
  comissaoPerc: string
  projecao: ProjecaoNr1
  nr1ValorIndividual: number
  valorIndividualNum: number
}

const buildFilename = (): string => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`
  return `calculadora-nr1-${stamp}.pdf`
}

const formatGeneratedAt = (): string =>
  new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

export function buildCalculadoraNr1Document(
  input: BuildCalculadoraNr1DocumentInput
): DocumentExportConfig {
  const { projecao, nr1ValorIndividual, valorIndividualNum } = input
  const nr1Detail = `${projecao.vidas} (Vidas) x ${fmtMoeda(nr1ValorIndividual)} (Valor individual NR-1)`

  return {
    title: 'Projeção de comissionamento — PASI NR-1',
    subtitle: `${fmtMoeda(valorIndividualNum)} por vida · ${projecao.vidas} vidas`,
    generatedAt: formatGeneratedAt(),
    filename: buildFilename(),
    sections: [
      {
        type: 'keyValueList',
        title: 'Dados do subgrupo',
        items: [
          { label: 'Número de vidas', value: String(projecao.vidas) },
          { label: 'Valor individual (R$)', value: fmtMoeda(valorIndividualNum) },
          { label: 'Comissão (%)', value: `${input.comissaoPerc || '0'}%` },
        ],
      },
      {
        type: 'keyValueList',
        title: 'Resumo',
        items: [
          { label: 'Número de vidas', value: String(projecao.vidas) },
          { label: 'Prêmio base', value: fmtMoeda(projecao.base) },
          {
            label: 'PASI NR-1 adicional',
            value: fmtMoeda(projecao.nr1),
            detail: nr1Detail,
          },
          { label: 'Prêmio total', value: fmtMoeda(projecao.total) },
        ],
      },
      {
        type: 'comparison',
        left: {
          title: 'Sem PASI NR-1',
          items: [
            { label: 'Comissão mensal', value: fmtMoeda(projecao.sem) },
            { label: 'Comissão anual', value: fmtMoeda(projecao.sem * 12) },
          ],
        },
        right: {
          title: 'Com PASI NR-1',
          items: [
            { label: 'Comissão mensal', value: fmtMoeda(projecao.com) },
            { label: 'Comissão anual', value: fmtMoeda(projecao.com * 12) },
          ],
        },
      },
      {
        type: 'highlight',
        title: 'Ganho adicional com PASI NR-1',
        lines: [
          `+${fmtMoeda(projecao.difMensal)}/mês (+${projecao.aum.toFixed(1)}%)`,
        ],
      },
      {
        type: 'totals',
        items: [
          { label: 'Comissão mensal com PASI NR-1', value: fmtMoeda(projecao.com) },
          { label: 'Comissão anual com PASI NR-1', value: fmtMoeda(projecao.com * 12) },
        ],
      },
    ],
  }
}
